import { Link } from 'react-router-dom';
import { Bars, type BarDatum } from '../../components/Bars';
import type { TerritoryRow } from '../data-source/api/season';
import type { Analysis } from './useAnalysis';
import { format, scopeQuery } from './model';
import { countOf, WORDS } from '../lib/format';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { tr } from '../../i18n/runtime';

const m = defineMessages({
  narrow: { id: 'board.massifBars.narrow', defaultMessage: 'Сузить срез до массива', description: 'Подсказка у имени массива в столбчатой диаграмме: клик сужает срез экрана до этого массива' },
  share: { id: 'board.massifBars.share', defaultMessage: '{value} %', description: 'Значение столбца: доля отчётов массива, процент; {value} — число с одним знаком' },
  noWorks: { id: 'board.massifBars.noWorks', defaultMessage: 'работы не назначались', description: 'Пометка у массива без работ на полях вместо столбца' },
  note: { id: 'board.massifBars.note', defaultMessage: '{reported} / {total}', description: 'Подпись под строкой массива: сколько отчётов из скольких работ на полях; {reported} — число, {total} — число с названием («120 работ на полях»)' },
  noteOverdue: { id: 'board.massifBars.noteOverdue', defaultMessage: '{reported} / {total} · {overdue} после срока', description: 'Подпись под строкой массива, когда есть работы после срока; {overdue} — число работ без отчёта после срока' },
  title: { id: 'board.massifBars.title', defaultMessage: 'Массивы', description: 'Заголовок диаграммы по массивам района' },
  unit: { id: 'board.massifBars.unit', defaultMessage: '% отчётов', description: 'Единица диаграммы по массивам: доля работ на полях с отчётом' },
  denominator: { id: 'board.massifBars.denominator', defaultMessage: '{count, plural, one {работа на поле массива} few {работы на полях массива} many {работ на полях массива} other {работы на полях массива}}', description: 'Знаменатель диаграммы по массивам; слово после числа, без числа' },
  valueHeader: { id: 'board.massifBars.valueHeader', defaultMessage: 'Отчитались', description: 'Заголовок колонки значений в таблице-двойнике диаграммы' },
  captionApi: { id: 'board.massifBars.captionApi', defaultMessage: 'Имя массива сужает срез экрана. Порядок — по доле отчётов.', description: 'Подпись под диаграммой по массивам (серверные данные)' },
  captionDemo: { id: 'board.massifBars.captionDemo', defaultMessage: 'Имя массива открывает его карточку. Порядок — по доле отчётов.', description: 'Подпись под диаграммой по массивам (демо-данные)' },
});

/**
 * Массивы района — ранжированный горизонтальный бар вместо таблицы
 * (agroai-0yt.2). Величина — доля отчётов от обязательств массива; порядок —
 * по этой доле. Массив здесь — территориальный срез района, не оценка: у бара
 * нет «лучших» и «худших» цветов, только длина.
 *
 * Таблица-двойник — у рамки (`components/Bars`, кнопка «таблица»): те же
 * числа строками, включая массивы без обязательств.
 */
export function massifBarData(rows: ReadonlyArray<TerritoryRow>): Array<TerritoryRow & { share: number | null }> {
  return rows
    .map(row => ({ ...row, share: row.total > 0 ? (100 * row.reported) / row.total : null }))
    .sort((a, b) => (b.share ?? -1) - (a.share ?? -1) || a.name.localeCompare(b.name, 'ru'));
}

export function MassifBars({ a }: { a: Analysis }) {
  const t = useT();
  const district = scopeQuery(a.scope, { massifId: undefined, farmId: undefined });
  const data: BarDatum[] = massifBarData(a.territory).map(row => ({
    key: row.id,
    labelText: row.name,
    label: a.sourceKind === 'api'
      ? <button type="button" className="astra-link" onClick={() => a.setScope('massif', row.id)} title={t(m.narrow)}>{row.name}</button>
      : <Link to={`/massifs/${row.id}${district}`}>{row.name}</Link>,
    value: row.share,
    display: row.share === null ? '—' : t(m.share, { value: format(row.share, 1) }),
    absent: row.total === 0 ? t(m.noWorks) : undefined,
    note: row.total > 0 ? t(row.overdue ? m.noteOverdue : m.note, { reported: format(row.reported), total: countOf(row.total, WORDS.obligation), overdue: format(row.overdue) }) : undefined,
  }));
  return (
    <Bars
      title={t(m.title)}
      unit={t(m.unit)}
      denominator={tr(m.denominator, { count: 5 })}
      valueHeader={t(m.valueHeader)}
      max={100}
      maxDisplay={t(m.share, { value: '100' })}
      data={data}
      demo={a.sourceKind === 'fixtures'}
      caption={a.sourceKind === 'api' ? t(m.captionApi) : t(m.captionDemo)}
    />
  );
}
